import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  SelectChangeEvent,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { Appointment, AppointmentType } from 'src/constants/types';
import { CreateAppointmentModal } from 'src/modules/calendar/components/CreateAppointmentModal';
import { useGetAppointmentApi } from 'src/modules/calendar/api';
import SelectAppointmentModal from './SelectAppointmentModal';
import HtmlTooltip from 'src/components/HtmlTooltip/HtmlTooltip';
import { ButtonLikeSelect } from './ButtonLikeSelect.styles';
import { formatAppointmentDateRange } from '../../helpers/appointmentMessageHelpers';

const CREATE_OPTION = '__create__';
const SELECT_OPTION = '__select__';

type MessageEventControlProps = {
  appointments: Appointment[];
  appointmentTypes: AppointmentType[];
  companyId: string;
  appointmentId?: string;
  disabled?: boolean;
  loading?: boolean;
  onChangeAppointment: (appointmentId: string | undefined) => void;
  onRequestRefreshAppointments: () => void;
};

const MessageEventControl: React.FC<MessageEventControlProps> = ({
  appointments,
  appointmentTypes,
  companyId,
  appointmentId,
  disabled,
  loading,
  onChangeAppointment,
  onRequestRefreshAppointments,
}) => {
  const [createOpen, setCreateOpen] = useState(false);
  const [selectOpen, setSelectOpen] = useState(false);
  const [resolved, setResolved] = useState<Appointment | null>(null);
  const [resolving, setResolving] = useState(false);
  const getAppointment = useGetAppointmentApi();

  const hasAppointment = !!appointmentId;
  const attachedFromList = appointments.find(a => a.id === appointmentId) ?? null;

  useEffect(() => {
    if (!appointmentId || attachedFromList) {
      setResolved(null);
      return;
    }
    let active = true;
    setResolving(true);
    getAppointment(appointmentId, companyId)
      .then(res => {
        if (active) setResolved(res.data);
      })
      .catch(() => {
        if (active) setResolved(null);
      })
      .finally(() => {
        if (active) setResolving(false);
      });
    return () => {
      active = false;
    };
  }, [appointmentId, attachedFromList, companyId, getAppointment]);

  const attached: Appointment | null = attachedFromList ?? resolved ?? null;
  const isResolving = hasAppointment && !attached && (!!loading || resolving);
  const isUnavailable = hasAppointment && !attached && !loading && !resolving;
  const displayName = attached?.name ?? (isUnavailable ? '(unavailable)' : '');

  const tooltipFields: { label: string; value: string }[] = [];
  if (attached) {
    const dateRange = formatAppointmentDateRange(attached.startDate, attached.endDate);
    if (dateRange) {
      tooltipFields.push({ label: 'When', value: dateRange });
    }
    const description = attached.description?.trim();
    if (description) {
      tooltipFields.push({ label: 'Description', value: description });
    }
    const location = attached.location?.trim();
    if (location) {
      tooltipFields.push({ label: 'Location', value: location });
    }
  }

  const tooltipContent =
    tooltipFields.length > 0 ? (
      <Stack spacing={1}>
        {tooltipFields.map(field => (
          <Box key={field.label}>
            <Typography
              variant="caption"
              display="block"
              fontWeight="fontWeightBold"
              color="text.secondary"
            >
              {field.label}
            </Typography>
            <Typography variant="body2">{field.value}</Typography>
          </Box>
        ))}
      </Stack>
    ) : null;

  const detach = () => {
    onChangeAppointment(undefined);
  };

  const handleChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    if (value === SELECT_OPTION) {
      setSelectOpen(true);
      return;
    }
    if (value === CREATE_OPTION) {
      setCreateOpen(true);
      return;
    }
    onChangeAppointment(value);
  };

  const chip = (
    <FormControl disabled={disabled}>
      <ButtonLikeSelect
        displayEmpty
        value=""
        onChange={handleChange}
        renderValue={() => (
          <>
            <CalendarTodayOutlinedIcon fontSize="small" />
            {isResolving ? (
              <CircularProgress size={14} aria-label="loading appointment" />
            ) : hasAppointment ? (
              `${displayName}`
            ) : (
              'Add Calendar Event'
            )}
          </>
        )}
      >
        <MenuItem value={SELECT_OPTION}><Button size="small" variant="contained" fullWidth>
          Choose Existing
        </Button></MenuItem>
        <MenuItem value={CREATE_OPTION} disabled={appointmentTypes.length === 0}>
          <Button size="small" variant="contained" fullWidth>
            Create New
          </Button>
        </MenuItem>
      </ButtonLikeSelect>
    </FormControl>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
      <Stack direction="row" spacing={0.5} alignItems="center">
        {tooltipContent ? (
          <HtmlTooltip title={tooltipContent} placement="top" arrow>
            {chip}
          </HtmlTooltip>
        ) : (
          chip
        )}

        {hasAppointment && (
          <Tooltip title="Remove appointment">
            <span>
              <IconButton
                aria-label="remove appointment"
                size="small"
                disabled={disabled}
                onClick={detach}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>

      {createOpen && (
        <CreateAppointmentModal
          show={createOpen}
          setShowModal={setCreateOpen}
          appointment={null}
          initialAppointment={{ name: '', companyId }}
          appointmentTypes={appointmentTypes}
          refreshAppointments={onRequestRefreshAppointments}
          slotStartDate={undefined}
          slotEndDate={undefined}
          hideCampaignLink
          hideContacts
          onCreated={created => {
            if (created?.id) {
              onChangeAppointment(created.id);
            }
          }}
        />
      )}

      {selectOpen && (
        <SelectAppointmentModal
          open={selectOpen}
          onClose={() => setSelectOpen(false)}
          onSelect={id => {
            setSelectOpen(false);
            onChangeAppointment(id);
          }}
        />
      )}
    </Box>
  );
};

export default MessageEventControl;
